import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api, { formatError } from "@/lib/api";
import { PageHeader, Chip, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Pencil, Trash, MagnifyingGlass, Archive, ArrowCounterClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELD_TYPES = [
    { value: "text", label: "Text" },
    { value: "textarea", label: "Textarea" },
    { value: "number", label: "Number" },
    { value: "amount", label: "Amount" },
    { value: "date", label: "Date" },
    { value: "datetime", label: "Date & Time" },
    { value: "time", label: "Time" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "dropdown", label: "Dropdown" },
    { value: "resident", label: "Resident (picker)" },
    { value: "flat", label: "Flat (picker)" },
    { value: "block", label: "Block" },
];

const STATUS_STYLE = {
    active: { variant: "success", label: "Active" },
    draft: { variant: "warn", label: "Draft" },
    archived: { variant: "neutral", label: "Archived" },
};

export default function DocumentTemplates() {
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterCategory, setFilterCategory] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [fieldsKey, setFieldsKey] = useState(Date.now());
    const [isApplying, setIsApplying] = useState(false);
    const [versionDialogOpen, setVersionDialogOpen] = useState(false);
    const [versionHistory, setVersionHistory] = useState(null);
    const [versionLoading, setVersionLoading] = useState(false);

    const [form, setForm] = useState({
        name: "",
        category: "meeting_notice",
        description: "",
        content: "",
        fields: []
    });

    const [fieldForm, setFieldForm] = useState({ name: "", label: "", type: "text", required: true });

    async function loadCategories() {
        try {
            const response = await api.get("/document-templates/categories");
            setCategories(response.data);
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function loadTemplates() {
        setLoading(true);
        try {
            const response = await api.get("/document-templates");
            setTemplates(response.data);
            setFilteredTemplates(response.data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadCategories();
        loadTemplates();
    }, []);

    useEffect(() => {
        let filtered = templates;

        if (filterCategory !== "all") {
            filtered = filtered.filter(t => t.category === filterCategory);
        }
        if (filterStatus !== "all") {
            filtered = filtered.filter(t => (t.status || "active") === filterStatus);
        }
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(t =>
                t.name.toLowerCase().includes(query) ||
                t.category.toLowerCase().includes(query)
            );
        }

        setFilteredTemplates(filtered);
    }, [searchQuery, filterCategory, filterStatus, templates]);

    // Smart template presets with auto-generated content and fields
    const getTemplatePreset = (category) => {
        const presets = {
            meeting_notice: {
                name: "Annual General Meeting Notice",
                content: `<h2>Notice of Annual General Meeting</h2>
<p>Dear Residents,</p>
<p>This is to inform you that the Annual General Meeting (AGM) of our Society will be held on <strong>{{date}}</strong> at <strong>{{time}}</strong> at <strong>{{venue}}</strong>.</p>
<h3>Agenda:</h3>
<ol>
<li>{{agenda_item_1}}</li>
<li>{{agenda_item_2}}</li>
<li>{{agenda_item_3}}</li>
</ol>
<p>All members are requested to attend the meeting.</p>
<p>Regards,<br>{{issued_by}}<br>{{designation}}</p>`,
                fields: [
                    { name: "date", label: "Meeting Date", type: "date", required: true },
                    { name: "time", label: "Meeting Time", type: "time", required: true },
                    { name: "venue", label: "Venue", type: "text", required: true },
                    { name: "agenda_item_1", label: "Agenda Item 1", type: "text", required: true },
                    { name: "agenda_item_2", label: "Agenda Item 2", type: "text", required: false },
                    { name: "agenda_item_3", label: "Agenda Item 3", type: "text", required: false },
                    { name: "issued_by", label: "Issued By", type: "text", required: true },
                    { name: "designation", label: "Designation", type: "text", required: true }
                ]
            },
            mom: {
                name: "Minutes of Meeting",
                content: `<h2>Minutes of Meeting</h2>
<p><strong>Meeting Date:</strong> {{date}}<br>
<strong>Time:</strong> {{time}}<br>
<strong>Venue:</strong> {{venue}}</p>
<h3>Attendees:</h3>
<p>{{attendees}}</p>
<h3>Discussions:</h3>
<p>{{discussions}}</p>
<h3>Decisions Made:</h3>
<ol>
<li>{{decision_1}}</li>
<li>{{decision_2}}</li>
</ol>
<h3>Action Items:</h3>
<p>{{action_items}}</p>
<p>Recorded by: {{recorded_by}}</p>`,
                fields: [
                    { name: "date", label: "Meeting Date", type: "date", required: true },
                    { name: "time", label: "Meeting Time", type: "time", required: true },
                    { name: "venue", label: "Venue", type: "text", required: true },
                    { name: "attendees", label: "Attendees", type: "textarea", required: true },
                    { name: "discussions", label: "Key Discussions", type: "textarea", required: true },
                    { name: "decision_1", label: "Decision 1", type: "text", required: false },
                    { name: "decision_2", label: "Decision 2", type: "text", required: false },
                    { name: "action_items", label: "Action Items", type: "textarea", required: false },
                    { name: "recorded_by", label: "Recorded By", type: "text", required: true }
                ]
            },
            resolution: {
                name: "Society Resolution",
                content: `<h2>Resolution</h2>
<p><strong>Resolution No:</strong> {{resolution_number}}<br>
<strong>Date:</strong> {{date}}</p>
<h3>Subject: {{subject}}</h3>
<p>{{description}}</p>
<p><strong>Resolved that:</strong> {{resolution_text}}</p>
<p><strong>Approved by:</strong><br>{{approved_by}}<br>{{designation}}</p>`,
                fields: [
                    { name: "resolution_number", label: "Resolution Number", type: "text", required: true },
                    { name: "date", label: "Date", type: "date", required: true },
                    { name: "subject", label: "Subject", type: "text", required: true },
                    { name: "description", label: "Description", type: "textarea", required: true },
                    { name: "resolution_text", label: "Resolution Text", type: "textarea", required: true },
                    { name: "approved_by", label: "Approved By", type: "text", required: true },
                    { name: "designation", label: "Designation", type: "text", required: true }
                ]
            },
            noc: {
                name: "No Objection Certificate",
                content: `<h2>No Objection Certificate (NOC)</h2>
<p><strong>Date:</strong> {{date}}<br>
<strong>NOC No:</strong> {{noc_number}}</p>
<p>To Whom It May Concern,</p>
<p>This is to certify that we have <strong>no objection</strong> to <strong>{{resident_name}}</strong>, resident of Flat No. <strong>{{flat_number}}</strong>, for the following purpose:</p>
<p><strong>Purpose:</strong> {{purpose}}</p>
<p>{{additional_details}}</p>
<p>Issued by:<br>{{issued_by}}<br>{{designation}}<br>{{contact}}</p>`,
                fields: [
                    { name: "date", label: "Date", type: "date", required: true },
                    { name: "noc_number", label: "NOC Number", type: "text", required: true },
                    { name: "resident_name", label: "Resident Name", type: "text", required: true },
                    { name: "flat_number", label: "Flat Number", type: "text", required: true },
                    { name: "purpose", label: "Purpose", type: "textarea", required: true },
                    { name: "additional_details", label: "Additional Details", type: "textarea", required: false },
                    { name: "issued_by", label: "Issued By", type: "text", required: true },
                    { name: "designation", label: "Designation", type: "text", required: true },
                    { name: "contact", label: "Contact", type: "text", required: false }
                ]
            },
            auth_letter: {
                name: "Authorization Letter",
                content: `<h2>Authorization Letter</h2>
<p><strong>Date:</strong> {{date}}</p>
<p>To Whom It May Concern,</p>
<p>I, <strong>{{authorizer_name}}</strong>, resident of Flat No. <strong>{{flat_number}}</strong>, hereby authorize <strong>{{authorized_person}}</strong> to {{purpose}} on my behalf.</p>
<p><strong>Valid from:</strong> {{valid_from}}<br>
<strong>Valid until:</strong> {{valid_until}}</p>
<p>Authorized Signature:<br>{{authorizer_name}}<br>{{contact}}</p>`,
                fields: [
                    { name: "date", label: "Date", type: "date", required: true },
                    { name: "authorizer_name", label: "Authorizer Name", type: "text", required: true },
                    { name: "flat_number", label: "Flat Number", type: "text", required: true },
                    { name: "authorized_person", label: "Authorized Person", type: "text", required: true },
                    { name: "purpose", label: "Purpose", type: "textarea", required: true },
                    { name: "valid_from", label: "Valid From", type: "date", required: true },
                    { name: "valid_until", label: "Valid Until", type: "date", required: true },
                    { name: "contact", label: "Contact", type: "text", required: false }
                ]
            },
            maintenance_bill: {
                name: "Maintenance Bill Template",
                content: `<h2>Maintenance Bill</h2>
<p><strong>Bill No:</strong> {{bill_number}}<br>
<strong>Date:</strong> {{date}}<br>
<strong>Flat No:</strong> {{flat_number}}</p>
<p><strong>Bill Period:</strong> {{period}}</p>
<table style="width:100%; border-collapse: collapse;">
<tr><th style="border:1px solid #ddd; padding:8px;">Description</th><th style="border:1px solid #ddd; padding:8px;">Amount</th></tr>
<tr><td style="border:1px solid #ddd; padding:8px;">Maintenance Charges</td><td style="border:1px solid #ddd; padding:8px;">{{maintenance_amount}}</td></tr>
<tr><td style="border:1px solid #ddd; padding:8px;">Water Charges</td><td style="border:1px solid #ddd; padding:8px;">{{water_charges}}</td></tr>
<tr><td style="border:1px solid #ddd; padding:8px;">Other Charges</td><td style="border:1px solid #ddd; padding:8px;">{{other_charges}}</td></tr>
<tr><td style="border:1px solid #ddd; padding:8px;"><strong>Total</strong></td><td style="border:1px solid #ddd; padding:8px;"><strong>{{total_amount}}</strong></td></tr>
</table>
<p>Please pay by {{due_date}}</p>`,
                fields: [
                    { name: "bill_number", label: "Bill Number", type: "text", required: true },
                    { name: "date", label: "Date", type: "date", required: true },
                    { name: "flat_number", label: "Flat Number", type: "text", required: true },
                    { name: "period", label: "Bill Period", type: "text", required: true },
                    { name: "maintenance_amount", label: "Maintenance Amount", type: "amount", required: true },
                    { name: "water_charges", label: "Water Charges", type: "amount", required: false },
                    { name: "other_charges", label: "Other Charges", type: "amount", required: false },
                    { name: "total_amount", label: "Total Amount", type: "amount", required: true },
                    { name: "due_date", label: "Due Date", type: "date", required: true }
                ]
            },
            demand_letter: {
                name: "Payment Demand Letter",
                content: `<h2>Payment Demand Letter</h2>
<p><strong>Date:</strong> {{date}}<br>
<strong>To:</strong> {{resident_name}}<br>
<strong>Flat No:</strong> {{flat_number}}</p>
<p>Dear Resident,</p>
<p>This is to inform you that your maintenance dues for the period <strong>{{period}}</strong> amounting to <strong>₹{{amount}}</strong> are pending.</p>
<p>You are requested to clear the dues by <strong>{{due_date}}</strong> to avoid further action.</p>
<p>Outstanding Amount: <strong>₹{{amount}}</strong></p>
<p>Regards,<br>{{issued_by}}<br>{{designation}}</p>
<p><strong>Authorized By:</strong><br>{{authorizer_name}}<br>{{authorizer_designation}}</p>`,
                fields: [
                    { name: "date", label: "Date", type: "date", required: true },
                    { name: "resident_name", label: "Resident Name", type: "text", required: true },
                    { name: "flat_number", label: "Flat Number", type: "text", required: true },
                    { name: "period", label: "Period", type: "text", required: true },
                    { name: "amount", label: "Amount", type: "amount", required: true },
                    { name: "due_date", label: "Due Date", type: "date", required: true },
                    { name: "issued_by", label: "Issued By", type: "text", required: true },
                    { name: "designation", label: "Designation", type: "text", required: true },
                    { name: "authorizer_name", label: "Authorizer Name", type: "text", required: false },
                    { name: "authorizer_designation", label: "Authorizer Designation", type: "text", required: false }
                ]
            },
            custom: {
                name: "Custom Document",
                content: `<h2>Document Title</h2>
<p>{{content}}</p>`,
                fields: [
                    { name: "content", label: "Content", type: "textarea", required: true }
                ]
            }
        };
        return presets[category] || presets.custom;
    };

    function openCreateDialog() {
        setEditingTemplate(null);
        const preset = getTemplatePreset("meeting_notice");
        setForm({
            name: preset.name,
            category: "meeting_notice",
            description: "",
            content: preset.content,
            fields: preset.fields
        });
        setOpenDialog(true);
    }

    function handleCategoryChange(newCategory) {
        setIsApplying(true);
        setTimeout(() => {
            const preset = getTemplatePreset(newCategory);
            setForm(prev => ({
                ...prev,
                name: preset.name,
                category: newCategory,
                content: preset.content,
                fields: preset.fields
            }));
            setFieldsKey(Date.now());
            setIsApplying(false);
        }, 50);
    }

    function openEditDialog(template) {
        setEditingTemplate(template);
        setForm({
            name: template.name,
            category: template.category,
            description: template.description || "",
            content: template.content,
            fields: template.fields || []
        });
        setOpenDialog(true);
    }

    function addField() {
        if (!fieldForm.name || !fieldForm.label) {
            toast.error("Field name and label are required");
            return;
        }
        setForm({
            ...form,
            fields: [...form.fields, { ...fieldForm }]
        });
        setFieldForm({ name: "", label: "", type: "text", required: true });
    }

    function removeField(index) {
        setForm({
            ...form,
            fields: form.fields.filter((_, i) => i !== index)
        });
    }

    async function saveTemplate(e) {
        e.preventDefault();
        try {
            if (editingTemplate) {
                if (editingTemplate.status === "archived") {
                    toast.error("Activate this template before editing it");
                    return;
                }
                await api.patch(`/document-templates/${editingTemplate.id}`, {
                    name: form.name, category: form.category, description: form.description,
                    content: form.content, fields: form.fields,
                });
                toast.success("Template updated — a new version was saved");
            } else {
                await api.post("/document-templates", form);
                toast.success("Template created successfully");
            }
            setOpenDialog(false);
            loadTemplates();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function deleteTemplate(template) {
        if (!confirm(`Delete template "${template.name}"?`)) return;
        try {
            await api.delete(`/document-templates/${template.id}`);
            toast.success("Template deleted successfully");
            loadTemplates();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function toggleArchive(template) {
        try {
            if (template.status === "archived") {
                await api.post(`/document-templates/${template.id}/activate`);
                toast.success("Template activated");
            } else {
                await api.post(`/document-templates/${template.id}/archive`);
                toast.success("Template archived");
            }
            loadTemplates();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function openVersionHistory(template) {
        setVersionDialogOpen(true);
        setVersionLoading(true);
        setVersionHistory(null);
        try {
            const r = await api.get(`/document-templates/${template.id}/versions`);
            setVersionHistory(r.data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setVersionLoading(false);
        }
    }

    const getCategoryInfo = (categoryId) => {
        return categories.find(c => c.id === categoryId) || { name: categoryId, icon: "📄" };
    };

    if (loading) return <LoadingState />;

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Documents"
                title="Document Templates"
                description="Create and manage reusable document templates"
                actions={
                    <Button onClick={openCreateDialog} className="rounded-full" data-testid="create-template-button">
                        <Plus size={16} className="mr-2" />
                        Create Template
                    </Button>
                }
            />

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                        <MagnifyingGlass size={20} className="text-brand-inkSoft" />
                        <Input
                            data-testid="template-search-input"
                            placeholder="Search templates..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="flex-1"
                        />
                    </div>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                        <SelectTrigger data-testid="template-category-filter">
                            <SelectValue placeholder="Filter by category" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>
                                    {cat.icon} {cat.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger data-testid="template-status-filter">
                            <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Templates Grid */}
            {filteredTemplates.length === 0 ? (
                <div className="bg-white border border-brand-line rounded-sm p-12 text-center" data-testid="templates-empty-state">
                    <FileText size={48} className="mx-auto text-brand-inkSoft mb-4" />
                    <p className="text-brand-inkSoft mb-4">
                        {searchQuery || filterCategory !== "all" || filterStatus !== "all" ? "No templates found" : "No templates created yet"}
                    </p>
                    <Button onClick={openCreateDialog} className="rounded-full">
                        <Plus size={16} className="mr-2" />
                        Create First Template
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="templates-grid">
                    {filteredTemplates.map((template) => {
                        const categoryInfo = getCategoryInfo(template.category);
                        const statusInfo = STATUS_STYLE[template.status || "active"];
                        return (
                            <div key={template.id} className="bg-white border border-brand-line rounded-sm p-6 hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-2xl">
                                            {categoryInfo.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-heading text-lg font-semibold text-brand-ink">
                                                {template.name}
                                            </h3>
                                            <p className="text-sm text-brand-inkSoft">{categoryInfo.name}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                    <Chip variant={statusInfo.variant} className="text-xs">{statusInfo.label}</Chip>
                                    <Chip variant="default" className="text-xs">v{template.version || 1}</Chip>
                                    {(template.usage_count || 0) > 0 && (
                                        <span className="text-xs text-brand-inkSoft">Used {template.usage_count}x</span>
                                    )}
                                </div>

                                {template.description && (
                                    <p className="text-sm text-brand-inkSoft mb-3">{template.description}</p>
                                )}

                                <div className="mb-4 pb-4 border-b border-brand-line">
                                    <p className="text-sm text-brand-inkSoft mb-2">Fields: {template.fields?.length || 0}</p>
                                    <div className="flex flex-wrap gap-1">
                                        {template.fields?.slice(0, 3).map((field, idx) => (
                                            <Chip key={idx} variant="default" className="text-xs">
                                                {field.label}
                                            </Chip>
                                        ))}
                                        {template.fields?.length > 3 && (
                                            <span className="text-xs text-brand-inkSoft">+{template.fields.length - 3} more</span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex gap-2 flex-wrap">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openEditDialog(template)}
                                        className="flex-1 rounded-full"
                                        data-testid={`edit-template-${template.id}`}
                                    >
                                        <Pencil size={14} className="mr-1" />
                                        Edit
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openVersionHistory(template)}
                                        className="rounded-full"
                                        data-testid={`version-history-${template.id}`}
                                        title="Version history"
                                    >
                                        <ClockCounterClockwise size={14} />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => toggleArchive(template)}
                                        className="rounded-full"
                                        data-testid={`archive-template-${template.id}`}
                                        title={template.status === "archived" ? "Activate" : "Archive"}
                                    >
                                        {template.status === "archived" ? <ArrowCounterClockwise size={14} /> : <Archive size={14} />}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => deleteTemplate(template)}
                                        className="rounded-full border-[#FECACA] text-[#DC2626] hover:bg-[#FEF2F2]"
                                        data-testid={`delete-template-${template.id}`}
                                        title={template.usage_count > 0 ? "Has generated documents — archive instead" : "Delete"}
                                    >
                                        <Trash size={14} />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create/Edit Template Dialog */}
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto transition-opacity duration-200" key={fieldsKey}>
                    <DialogHeader>
                        <DialogTitle>{editingTemplate ? "Edit Template" : "Create Template"}</DialogTitle>
                    </DialogHeader>

                    {!editingTemplate && (
                        <div className="bg-brand-sage border border-brand-action/20 rounded-lg p-4 mb-4">
                            <p className="text-sm text-brand-ink">
                                <strong>Smart Template:</strong> Select a category to auto-generate professional format with matching fields.
                                Change category anytime to switch template type!
                            </p>
                        </div>
                    )}

                    {editingTemplate && (
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4">
                            <p className="text-sm text-amber-900">
                                <strong>Editing Mode:</strong> Saving will create a new version. Documents already generated
                                from this template keep pointing at their original version and will not change.
                            </p>
                        </div>
                    )}

                    <form onSubmit={saveTemplate} className="space-y-6" data-testid="template-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Template Name *</Label>
                                <Input
                                    required
                                    data-testid="template-name-input"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="e.g., Annual General Meeting Notice"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Category *</Label>
                                <div className="flex gap-2">
                                    <Select
                                        value={form.category}
                                        onValueChange={(v) => setForm({ ...form, category: v })}
                                    >
                                        <SelectTrigger data-testid="template-category-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categories.map(cat => (
                                                <SelectItem key={cat.id} value={cat.id}>
                                                    {cat.icon} {cat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {!editingTemplate && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => handleCategoryChange(form.category)}
                                            disabled={isApplying}
                                            className="whitespace-nowrap"
                                            data-testid="apply-template-preset-button"
                                        >
                                            {isApplying ? "Applying..." : "Apply Template"}
                                        </Button>
                                    )}
                                </div>
                                {!editingTemplate && (
                                    <p className="text-xs text-brand-inkSoft">
                                        Select category, then click "Apply Template" to load format and fields
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Description (Optional)</Label>
                            <Textarea
                                data-testid="template-description-input"
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="Internal note about when/how to use this template"
                                rows={2}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Template Content (HTML) *</Label>
                            <div className="border border-brand-line rounded-sm">
                                <ReactQuill
                                    theme="snow"
                                    value={form.content}
                                    onChange={(content) => setForm({ ...form, content })}
                                    className="bg-white"
                                    modules={{
                                        toolbar: [
                                            [{ 'header': [1, 2, 3, false] }],
                                            ['bold', 'italic', 'underline'],
                                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                            ['link'],
                                            ['clean']
                                        ]
                                    }}
                                />
                            </div>
                            <p className="text-xs text-brand-inkSoft">
                                Use placeholders like {`{{field_name}}`} for dynamic content
                            </p>
                        </div>

                        <div className="space-y-4" key={fieldsKey}>
                            <div className="flex items-center justify-between">
                                <Label>Template Fields</Label>
                                <p className="text-xs text-brand-inkSoft">
                                    {form.fields.length} fields defined
                                </p>
                            </div>

                            {form.fields.map((field, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-sm">
                                    <div className="flex-1 grid grid-cols-4 gap-2 text-sm">
                                        <div>
                                            <span className="text-brand-inkSoft">Name:</span> <code className="text-brand-ink">{field.name}</code>
                                        </div>
                                        <div>
                                            <span className="text-brand-inkSoft">Label:</span> {field.label}
                                        </div>
                                        <div>
                                            <span className="text-brand-inkSoft">Type:</span> {field.type}
                                        </div>
                                        <div>
                                            <span className="text-brand-inkSoft">Required:</span> {field.required === false ? "No" : "Yes"}
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => removeField(idx)}
                                        className="text-red-600"
                                        data-testid={`remove-field-${idx}`}
                                    >
                                        <Trash size={16} />
                                    </Button>
                                </div>
                            ))}

                            <div className="border border-dashed border-brand-line rounded-sm p-4 bg-gray-50">
                                <p className="text-sm font-medium text-brand-ink mb-3">Add New Field</p>
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
                                    <Input
                                        data-testid="new-field-name-input"
                                        placeholder="Field name (e.g., date)"
                                        value={fieldForm.name}
                                        onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                                    />
                                    <Input
                                        data-testid="new-field-label-input"
                                        placeholder="Field label (e.g., Meeting Date)"
                                        value={fieldForm.label}
                                        onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
                                    />
                                    <Select value={fieldForm.type} onValueChange={(v) => setFieldForm({ ...fieldForm, type: v })}>
                                        <SelectTrigger data-testid="new-field-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {FIELD_TYPES.map(ft => (
                                                <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="new-field-required"
                                            checked={fieldForm.required}
                                            onCheckedChange={(v) => setFieldForm({ ...fieldForm, required: !!v })}
                                        />
                                        <label htmlFor="new-field-required" className="text-sm">Required</label>
                                    </div>
                                    <Button type="button" onClick={addField} size="sm" className="rounded-full" data-testid="add-field-button">
                                        <Plus size={14} className="mr-1" />
                                        Add
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="bg-brand-action hover:bg-brand-actionHover text-white" data-testid="save-template-button">
                                {editingTemplate ? "Save New Version" : "Create Template"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Version History Dialog */}
            <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="version-history-dialog">
                    <DialogHeader>
                        <DialogTitle>Version History</DialogTitle>
                    </DialogHeader>
                    {versionLoading ? (
                        <LoadingState />
                    ) : versionHistory ? (
                        <div className="space-y-3">
                            <div className="p-3 bg-brand-sage rounded-sm">
                                <p className="text-sm font-medium">Current version: v{versionHistory.current_version}</p>
                            </div>
                            {versionHistory.history.length === 0 ? (
                                <p className="text-sm text-brand-inkSoft">No prior versions — this template has never been edited.</p>
                            ) : (
                                versionHistory.history.map((h) => (
                                    <div key={h.id} className="border border-brand-line rounded-sm p-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium text-sm">v{h.version} — {h.name}</span>
                                            <span className="text-xs text-brand-inkSoft">
                                                Superseded {new Date(h.superseded_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="text-xs text-brand-inkSoft">{h.fields?.length || 0} fields</p>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
